import { inject, Injectable } from "@angular/core";
import { HttpContext } from "@angular/common/http";
import { Action } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, finalize, map, merge, mergeMap, of, Subject, switchMap, withLatestFrom } from "rxjs";
import { DrawingAttachment, DrawingClient } from "src/api-service";
import { UPLOAD_PROGRESS } from "src/app/shared/http/upload-progress.interceptor";
import { normalizeError } from "../../../app-store-error.types";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { QuoteActions } from "../quote.actions";
import { DrawingActions } from "./drawing.actions";

@Injectable()
export class DrawingEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly drawingClient = inject(DrawingClient);

    // ******** LOAD ******** //
    loadDrawing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DrawingActions.loadDrawing),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId }, selectedCompany]) =>
                this.drawingClient.get(selectedCompany.id, quoteId).pipe(
                    map(drawing => DrawingActions.loadDrawingSuccess({ drawing })),
                    catchError(error => of(DrawingActions.loadDrawingFailure({
                        error: normalizeError(error),
                        toastKey: "QUOTE.DRAWING.TOAST.LOAD_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // ******** UPLOAD ATTACHMENT ******** //
    // mergeMap (not switchMap) so dropping multiple files uploads them all in parallel.
    // Real per-file progress is forwarded by uploadProgressInterceptor via the UPLOAD_PROGRESS context.
    uploadAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DrawingActions.uploadAttachment),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            mergeMap(([{ tempId, quoteId, file, kind }, selectedCompany]) => {
                const progress$ = new Subject<Action>();
                const context = new HttpContext().set(UPLOAD_PROGRESS, (loaded, total) =>
                    progress$.next(DrawingActions.uploadAttachmentProgress({
                        tempId,
                        progress: total ? Math.min(99, Math.round((100 * loaded) / total)) : 0,
                    })),
                );

                const upload$ = this.drawingClient
                    .uploadAttachment(selectedCompany?.id ?? '', quoteId, { data: file, fileName: file.name }, kind, context)
                    .pipe(
                        map<DrawingAttachment, Action>(attachment => DrawingActions.uploadAttachmentSuccess({
                            tempId,
                            attachment,
                            toastKey: "QUOTE.DRAWING.TOAST.UPLOAD_SUCCESS",
                        })),
                        catchError(error => of(DrawingActions.uploadAttachmentFailure({
                            tempId,
                            error: normalizeError(error),
                            toastKey: "QUOTE.DRAWING.TOAST.UPLOAD_ERROR",
                        }))),
                        finalize(() => progress$.complete()),
                    );

                return merge(progress$, upload$);
            }),
        ),
    );

    // ******** DELETE ATTACHMENT ******** //
    deleteAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DrawingActions.deleteAttachment),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, attachmentId }, selectedCompany]) =>
                this.drawingClient.deleteAttachment(selectedCompany.id, quoteId, attachmentId).pipe(
                    map(() => DrawingActions.deleteAttachmentSuccess({
                        attachmentId,
                        toastKey: "QUOTE.DRAWING.TOAST.DELETE_SUCCESS",
                    })),
                    catchError(error => of(DrawingActions.deleteAttachmentFailure({
                        error: normalizeError(error),
                        toastKey: "QUOTE.DRAWING.TOAST.DELETE_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // ******** SEND FOR DRAWING ******** //
    sendForDrawing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DrawingActions.sendForDrawing),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, subject, message }, selectedCompany]) =>
                this.drawingClient.sendForDrawing(selectedCompany.id, quoteId, { subject, message }).pipe(
                    mergeMap(drawing => [
                        DrawingActions.sendForDrawingSuccess({
                            drawing,
                            toastKey: "QUOTE.DRAWING.TOAST.SEND_FOR_DRAWING_SUCCESS",
                        }),
                        QuoteActions.initializeQuote({ quoteId }),
                    ]),
                    catchError(error => of(DrawingActions.sendForDrawingFailure({
                        error: normalizeError(error),
                        toastKey: "QUOTE.DRAWING.TOAST.SEND_FOR_DRAWING_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // ******** START DRAWING ******** //
    startDrawing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DrawingActions.startDrawing),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, estimatedCompletion, drawerId, subject, message }, selectedCompany]) =>
                this.drawingClient.startDrawing(selectedCompany.id, quoteId, { estimatedCompletion, drawerId, subject, message }).pipe(
                    mergeMap(drawing => [
                        DrawingActions.startDrawingSuccess({
                            drawing,
                            toastKey: "QUOTE.DRAWING.TOAST.START_DRAWING_SUCCESS",
                        }),
                        QuoteActions.initializeQuote({ quoteId }),
                    ]),
                    catchError(error => of(DrawingActions.startDrawingFailure({
                        error: normalizeError(error),
                        toastKey: "QUOTE.DRAWING.TOAST.START_DRAWING_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // ******** REVOKE DRAWING ******** //
    revokeDrawing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DrawingActions.revokeDrawing),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId }, selectedCompany]) =>
                this.drawingClient.revoke(selectedCompany.id, quoteId).pipe(
                    mergeMap(drawing => [
                        DrawingActions.revokeDrawingSuccess({
                            drawing,
                            toastKey: "QUOTE.DRAWING.TOAST.REVOKE_SUCCESS",
                        }),
                        QuoteActions.initializeQuote({ quoteId }),
                    ]),
                    catchError(error => of(DrawingActions.revokeDrawingFailure({
                        error: normalizeError(error),
                        toastKey: "QUOTE.DRAWING.TOAST.REVOKE_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // ******** COMPLETE DRAWING ******** //
    completeDrawing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DrawingActions.completeDrawing),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, sharepointUrl, subject, message }, selectedCompany]) =>
                this.drawingClient.completeDrawing(selectedCompany.id, quoteId, { sharepointUrl, subject, message }).pipe(
                    mergeMap(drawing => [
                        DrawingActions.completeDrawingSuccess({
                            drawing,
                            toastKey: "QUOTE.DRAWING.TOAST.COMPLETE_DRAWING_SUCCESS",
                        }),
                        QuoteActions.initializeQuote({ quoteId }),
                    ]),
                    catchError(error => of(DrawingActions.completeDrawingFailure({
                        error: normalizeError(error),
                        toastKey: "QUOTE.DRAWING.TOAST.COMPLETE_DRAWING_ERROR",
                    }))),
                ),
            ),
        ),
    );
}
